import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'
import {
  getSearchResult,
  getSearchHistoryList,
  getSearchKeywordList,
  getSearchConditionList,
  postSearchConditionList,
  patchSearchConditionList,
  deleteSearchConditionList,
} from '../../apis/search'
import {
  SearchRequestParams,
  SearchResponseParams,
  GetKeywordRequestParams,
  KeywordParam,
  GetWordCloudRequestParams,
  GetWordCloudResponseParams,
  PostSearchConditionParams,
  PatchSearchConditionParams,
  GetSearchConditionParams,
  SearchConditionDetails,
  SearchSortRequest,
} from '../../types/search'

interface searchListState {
  isLoading: boolean
  isWordLoading: boolean
  searchResult: SearchResponseParams
  searchHistory: string[]
  searchKeyword: KeywordParam[]
  searchWordCloud: GetWordCloudResponseParams
  searchCondition: SearchConditionDetails[]
}

const initialState: searchListState = {
  isLoading: false,
  isWordLoading: false,
  searchResult: {
    summary: undefined,
    result_list: undefined,
    tags: [],
    search_result: true,
    search_request: undefined,
  },
  searchHistory: [],
  searchKeyword: [],
  searchWordCloud: {
    image: '',
  },
  searchCondition: [],
}

const searchListSlice = createSlice({
  name: 'searchList',
  initialState,
  reducers: {
    startSearchLoading: (state) => {
      state.isLoading = true
    },
    startWordLoading: (state) => {
      state.isWordLoading = true
    },
    initialSearchResult: (state) => {
      state.searchResult = {
        summary: undefined,
        result_list: undefined,
        tags: [],
        search_result: true,
        search_request: undefined,
      }
      return state
    },
    getSearchFailure: (state) => {
      state.searchResult = {
        summary: undefined,
        result_list: undefined,
        tags: [],
        search_result: false,
        search_request: undefined,
      }
      return state
    },
    getHistorySuccess: (state, action: PayloadAction<string[]>) => {
      state.searchHistory = action.payload
      return state
    },
    getKeywordSuccess: (state, action: PayloadAction<KeywordParam[]>) => {
      state.isWordLoading = false
      state.searchKeyword = action.payload
      return state
    },
    updateResult: (state, action: PayloadAction<SearchResponseParams>) => {
      state.isLoading = false
      state.searchResult.summary = action.payload.summary
      state.searchResult.result_list = action.payload.result_list
      state.searchResult.tags = action.payload.tags
      state.searchResult.search_result = true
      return state
    },
    resetResult: (state) => {
      state.searchResult.result_list = undefined
      return state
    },
    updateSortResult: (state, action: PayloadAction<SearchSortRequest>) => {
      state.searchResult.result_list = action.payload.result_list
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      state.isWordLoading = false
      return state
    },
    getConditionSuccess: (state, action: PayloadAction<GetSearchConditionParams>) => {
      state.searchCondition = action.payload.search_condition_details
      return state
    },
    updateSearchRequestParams: (state, action: PayloadAction<SearchRequestParams>) => {
      state.searchResult.search_request = action.payload
      return state
    },
  },
})

export const {
  startSearchLoading,
  startWordLoading,
  initialSearchResult,
  getSearchFailure,
  requestFailure,
  updateResult,
  getHistorySuccess,
  getKeywordSuccess,
  getConditionSuccess,
  updateSearchRequestParams,
  resetResult,
  updateSortResult,
} = searchListSlice.actions

export const searchListReducer = searchListSlice.reducer

export const fetchHistory = (
): AppThunk => async (dispatch) => {
  getSearchHistoryList()
    .then((result) => {
      dispatch(getHistorySuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchKeyword = (
  params: GetKeywordRequestParams,
): AppThunk => async (dispatch) => {
  dispatch(startWordLoading())
  getSearchKeywordList(params)
    .then((result) => {
      dispatch(getKeywordSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
      dispatch(requestFailure())
    })
}

export const fetchSearchRequest = (
  params: SearchRequestParams,
  callback: (result: []) => void,
): AppThunk => async (dispatch) => {
  dispatch(startSearchLoading())
  dispatch(initialSearchResult())
  getSearchResult(params)
    .then((result) => {
      dispatch(updateResult(result))
      dispatch(updateSearchRequestParams(params))
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(getSearchFailure())
      dispatch(setErrors(error))
      callback(error)
    })
}

export const updateSearchResult = (
  params: SearchSortRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(updateSortResult(params))
  callback()
}

export const fetchSearchCondition = (
): AppThunk => async (dispatch) => {
  getSearchConditionList()
    .then((result) => {
      dispatch(getConditionSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(getSearchFailure())
      dispatch(setErrors(error))
    })
}

export const createSearchCondition = (
  params: PostSearchConditionParams,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  postSearchConditionList(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const updateSearchCondition = (
  params: PatchSearchConditionParams,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchSearchConditionList(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const delSearchCondition = (
  params: number,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  deleteSearchConditionList(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}
